import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, Download, BookOpen, CheckCircle, Users, Shield, FileCheck, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const CompliancePage = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Documents organized by category
  const complianceManuals = [
    { name: 'Compulsory Compliance Documents Guide', file: 'Compulsory-Compliance-Documents-Guide-29.05.2024.pdf', icon: FileCheck },
    { name: 'Broker Policies and Procedures Manual', file: 'Broker-Policy-and-Procedure-Manual-18.06.2024.pdf', icon: BookOpen },
    { name: 'Consumer Lending Compliance Handbook', file: 'Consumer-Lending-Compliance-Handbook-18.06.2024-1.pdf', icon: FileText },
    { name: 'Broker Code of Conduct', file: 'Broker-Code-of-Conduct-03.04.2023.pdf', icon: Shield },
    { name: 'Inappropriate Behaviour and Harassment Code of Conduct', file: 'Inappropriate-Behaviour-and-Harassment-Code-of-Conduct-18.05.2023.pdf', icon: AlertCircle },
  ];

  const residentialTemplates = [
    { name: 'Credit Guide & Privacy Combo (DOC)', file: 'Credit-Guide-and-Privacy-Consent-manual-docs-v2-Dec-2020.docx' },
    { name: 'Credit Guide & Privacy Combo (PDF)', file: 'Credit-Guide-and-Privacy-Consent-manual-docs-v2-Dec-2020.pdf' },
    { name: 'Client Needs Analysis/Fact Find (DOC)', file: 'Client-Profile-Fact-Find-V2-18.12.20.docx' },
    { name: 'Client Needs Analysis/Fact Find (PDF)', file: 'Client-Profile-Fact-Find-Manual-V2-18.12.20.pdf' },
    { name: 'Employment Verification (PDF)', file: 'Employment-Verification-Form-Fillable-28.5.24-1.pdf' },
    { name: 'Product Comparison (DOC)', file: 'Product-Comparison-manual-v2-Dec-2020.docx' },
    { name: 'Product Comparison (PDF)', file: 'Product-Comparison-manual-v2-Dec-2020.pdf' },
    { name: 'Credit Proposal (DOC)', file: 'Credit-Proposal-V2-18.12.20.docx' },
    { name: 'Credit Proposal (PDF)', file: 'Credit-Proposal-Manual-V2-18.12.20.pdf' },
    { name: 'Preliminary Assessment and Recommendation (DOC)', file: 'Preliminary-Assessment-V3-01.10.2021.docx' },
    { name: 'Preliminary Assessment and Recommendation (PDF)', file: 'Preliminary-Assessment-V3-01.10.2021.pdf' },
    { name: 'Client Profile Recommendation (DOC)', file: 'CPR-v1-OCT21.docx' },
    { name: 'Client Profile Recommendation (PDF)', file: 'CPR-v1-OCT21.pdf' },
    { name: 'Client AML Risk Assessment (PDF)', file: 'Client-AML-Risk-Assessment-Form-v.29.1.2025.pdf' },
  ];

  const consumerLoansTemplates = [
    { name: 'Credit Guide Privacy Consent (DOC)', file: 'Asset-and-Personal-Loan-Credit-Guide-and-Privacy-Consent-manual-docs-v2-Dec-2020.docx' },
    { name: 'Credit Guide Privacy Consent (PDF)', file: 'Asset-and-Personal-Loan-Credit-Guide-and-Privacy-Consent-manual-docs-v2-Dec-2020.pdf' },
    { name: 'Asset / Personal Loan CPR (DOC)', file: 'Asset-and-Personal-Loan-CPR-Compliance-Template-Combo-v8-v.15.01.2025-FILLABLE-2.docx' },
    { name: 'Asset / Personal Loan CPR (PDF)', file: 'Asset-and-Personal-Loan-CPR-Compliance-Template-Combo-v8-v.15.01.2025-FILLABLE-2.pdf' },
    { name: 'Credit Quote (DOC)', file: 'Credit-Quote.docx' },
    { name: 'Product Comparison Manual (DOC)', file: 'Product-Comparison-manual-v2-Dec-20201.docx' },
    { name: 'Product Comparison Manual (PDF)', file: 'Product-Comparison-manual-v2-Dec-20201.pdf' },
  ];

  const authorityTemplates = [
    { name: 'Tax Return Authority (DOC)', file: 'Tax-Return-Authorisation-Authority.doc' },
    { name: 'Employment Authorization Authority (DOC)', file: 'Employment-Authorisation-Authority.doc' },
    { name: 'Account Authorisation Authority (DOC)', file: 'Account-Authorisation-Authority.doc' },
  ];

  const auditDocuments = [
    { name: 'Audit Checklist (PDF)', file: 'Audit-Checklist-for-Brokers-4.6.2024.pdf' },
    { name: 'Register Declaration', file: 'REGISTER-DECLARATION-DOCUMENT-fillable.pdf' },
    { name: 'Annual Broker Compliance Attestation Declaration', file: 'ANNUAL-LOAN-WRITER-COMPLIANCE-ATTESTATION-DECLARATION-FILLABLE.pdf' },
    { name: 'Members Registers & Marketing Audit Checklist', file: 'Members-Registers-Marketing-Checklist-Fillable.pdf' },
    { name: 'Policy and Procedure Attestation', file: 'Policies-and-Guides-Attestation-Fillable.pdf' },
  ];

  const registers = [
    { name: 'Breach Register', file: 'Member-Breach-Register.xlsx' },
    { name: 'Complaints Register', file: 'Complaints-Register-Template.xls' },
    { name: 'Conflict of Interest Register', file: 'Conflict-of-Interest-Register.xls' },
    { name: 'Referrers Register', file: 'Referrers-Register.docx' },
    { name: 'Soft Dollar Register', file: 'Soft_dollar_register.xls' },
  ];

  const advertisingDocs = [
    { name: 'Advertising Guidelines', file: 'Advertising-Guidelines-18.6.2024.pdf' },
    { name: 'Privacy Policy', file: 'Privacy-Policy-4.11.2024.pdf' },
    { name: 'Privacy Policy Templates', file: 'Privacy-Policy-Templates-4.11.2024.pdf' },
  ];

  const referrerDocs = [
    { name: "Writer's Own Referrers Policy & Procedure", file: 'Referrers-Writers-own-Referrers-Policy-Procedure-.pdf' },
    { name: 'Referrer Register', file: 'Referrers-Register (1).docx' },
    { name: 'How To Setup Your Own Referrers', file: 'How-to-set-up-your-own-Referrers.pdf' },
    { name: "Referrer Requirements For A Broker's Own Referrers", file: 'Referrer-Requirements-for-a-loan-writers-own-referrers.pdf' },
  ];

  const downloadFile = (filename) => {
    const link = document.createElement('a');
    link.href = `/compliance/${filename}`;
    link.download = filename;
    link.click();
  };

  const Section = ({ title, icon: Icon, children, sectionId }) => {
    const isExpanded = expandedSection === sectionId;
    
    return (
      <Card className="border-2 hover:border-orange-300 transition-all duration-300 mb-6">
        <CardHeader 
          className="cursor-pointer"
          onClick={() => toggleSection(sectionId)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Icon className="w-6 h-6 text-orange-600" />
              </div>
              <CardTitle className="text-xl font-bold text-slate-800">{title}</CardTitle>
            </div>
            {isExpanded ? (
              <ChevronUp className="w-6 h-6 text-orange-600" />
            ) : (
              <ChevronDown className="w-6 h-6 text-slate-400" />
            )}
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="pt-0">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  const DocumentList = ({ documents }) => (
    <div className="space-y-2">
      {documents.map((doc, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-orange-600" />
            <span className="text-slate-700 font-medium">{doc.name}</span>
          </div>
          <Button
            onClick={() => downloadFile(doc.file)}
            variant="outline"
            size="sm"
            className="border-orange-200 text-orange-600 hover:bg-orange-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-orange-600 via-red-500 to-pink-600 text-transparent bg-clip-text">
            Compliance Resources
          </h1>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-slate-700 mb-4">
              At Derwent Finance we see ourselves as your business partner, and as such, we recognise that whilst compliance plays a key role in your business, we know it can often seem daunting and confusing.
            </p>
            <p className="text-lg text-slate-700">
              The aim of this compliance section of our website, is to provide you all the tools and resources required, to meet your regulatory obligations and run a successful and sustainable business.
            </p>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Manuals', icon: BookOpen, color: 'blue' },
            { label: 'Templates', icon: FileText, color: 'green' },
            { label: 'Audits', icon: FileCheck, color: 'purple' },
            { label: 'Privacy', icon: Shield, color: 'red' }
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => toggleSection(item.label.toLowerCase())}
              className={`p-4 bg-${item.color}-50 rounded-lg hover:bg-${item.color}-100 transition-all border-2 border-${item.color}-200 hover:border-${item.color}-400`}
            >
              <item.icon className={`w-8 h-8 text-${item.color}-600 mx-auto mb-2`} />
              <p className={`text-sm font-semibold text-${item.color}-700`}>{item.label}</p>
            </button>
          ))}
        </div>

        {/* Compliance Manuals Section */}
        <Section title="Compliance Manuals" icon={BookOpen} sectionId="manuals">
          <div className="mb-4">
            <p className="text-slate-700 mb-4">
              The below manuals are essential for you to understand your compliance obligations. As a credit representative under the Derwent Finance licence, you are required to read these manuals to understand what is required from you, as a mortgage broker.
            </p>
          </div>
          <DocumentList documents={complianceManuals} />
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
            <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Responsible Lending Guidelines
            </h4>
            <p className="text-blue-800 mb-2">
              There are multiple regulatory guides that relates to all participants in the Financial Services Industry. These include but are not limited to RG209, RG234, RG203, RG205, RG267, RG273. It is strongly recommended that you read these guides. It is also recommended that you read the entire National Consumer Credit Protection (NCCP) Act and National Credit Code (NCC).
            </p>
            <p className="text-blue-800 font-semibold">
              For access to these guides refer to{' '}
              <a href="https://www.asic.gov.au" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                www.asic.gov.au
              </a>
            </p>
          </div>
        </Section>

        {/* Templates Section */}
        <Section title="Compliance Templates" icon={FileText} sectionId="templates">
          <p className="text-slate-700 mb-6">
            Best practice in relation to compliance documents is to use the documents generated in the system. If, however, you require a manual form, please see the documents listed below:
          </p>
          
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Residential Lending
            </h3>
            <DocumentList documents={residentialTemplates} />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Consumer Car Loans and Unsecured Personal Loans
            </h3>
            <DocumentList documents={consumerLoansTemplates} />
          </div>

          <div>
            <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Authority Templates
            </h3>
            <DocumentList documents={authorityTemplates} />
          </div>
        </Section>

        {/* Audits Section */}
        <Section title="Audits" icon={FileCheck} sectionId="audits">
          <div className="mb-6">
            <p className="text-slate-700 mb-4">
              There are three (3) types of audits we conduct at Derwent Finance and they are outlined below. Please see section 10 of the Broker Policy and Procedure Manual for our full guidelines.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Audit of Lending Files</h3>
            <p className="text-slate-700 mb-4">
              Annually, our Compliance Department will request up to six (6) loan files from each broker (depending on your volumes) for mandatory audits. We recommend that you complete an audit checklist on every file you write to ensure you are meeting your compliance requirements.
            </p>
            <DocumentList documents={auditDocuments.slice(0, 1)} />
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Business Certificates Audit</h3>
            <p className="text-slate-700 mb-4">
              You are required to ensure that your business certificates are always current. These certificates need to be renewed annually except for the AML CTF which is renewed every two (2) years:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4">
              <li>MFAA or FBAA Membership</li>
              <li>AFCA Membership</li>
              <li>Professional Indemnity Insurance</li>
              <li>AML & CTF Course</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Audit of Business Operations (Registers and Marketing)</h3>
            <p className="text-slate-700 mb-4">
              Annual audits are conducted on all Derwent Finance members' Business Registers and Marketing Material. To remain compliant there are certain registers that you must download and keep maintained. Below are the register templates:
            </p>
            <DocumentList documents={registers} />
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-bold text-slate-800 mb-3">Audit Documentation</h3>
            <DocumentList documents={auditDocuments.slice(1)} />
          </div>
        </Section>

        {/* Advertising & Privacy Section */}
        <Section title="Advertising Guidelines and Privacy Statements" icon={Shield} sectionId="advertising">
          <p className="text-slate-700 mb-4">
            It is extremely important that you check our "Advertising Guidelines" document to make sure that the tools and collateral you create are compliant. The "Advertising Guidelines" document will tell you the minimum you will need to do, to be compliant for your website, social media page/s, email signatures, business cards and any marketing material that you create.
          </p>
          <DocumentList documents={advertisingDocs} />
          
          <div className="mt-6 p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
            <h4 className="font-bold text-orange-900 mb-2">Privacy Policy Requirements</h4>
            <p className="text-orange-800">
              As a Credit Representative, brokers will handle an individual or entity's personal information with Credit Providers (CP) and Credit Reporting Bodies (CRB). Brokers are required to have a privacy and policy statement on your website and email signature. Please see section 27 of the Broker Policy and Procedure Manual for our full guidelines.
            </p>
          </div>
        </Section>

        {/* Referrers Section */}
        <Section title="How To Onboard Your Own Referrers" icon={Users} sectionId="referrers">
          <p className="text-slate-700 mb-4">
            If you have your own Referrer/s, there are rules that you are required to adhere to. The Referrer Policy explains the rules around Referrers and the procedures for on-boarding Referrers compliantly.
          </p>
          <p className="text-slate-700 mb-4">
            Please see section 15 of the Broker Policy and Procedure Manual for our full Referrer Policy and section 28 for Advertising and Marketing guidelines.
          </p>
          <DocumentList documents={referrerDocs} />
        </Section>

        {/* CPD Section */}
        <Section title="Continuous Professional Development (CPD)" icon={CheckCircle} sectionId="cpd">
          <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
            <h3 className="text-xl font-bold text-slate-800 mb-4">CPD Requirements</h3>
            <p className="text-slate-700 mb-4">
              The updating of skills and knowledge is critical in keeping abreast of industry changes, maintaining professional competence and ensuring that qualifications do not become obsolete.
            </p>
            <p className="text-slate-700 mb-4">
              Brokers are required to complete a minimum of:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mb-4">
              <li><strong>25 hours</strong> of mandatory continuing professional points each year to renew their industry membership with <strong>FBAA</strong></li>
              <li><strong>30 hours</strong> to renew with <strong>MFAA</strong></li>
            </ul>
            <p className="text-slate-700">
              For further information on CPD, please see section 10 (vi) of the Broker Policy and Procedure Manual for our full guidelines.
            </p>
          </div>
        </Section>
      </main>

      <Footer />
    </div>
  );
};

export default CompliancePage;
